<template>
  <validation-observer ref="providerObserver">
    <v-container fluid>
      <v-card outlined class="mt-3">
        <v-card-title>{{ $t('findInStore.settings.general') }}</v-card-title>
        <v-container fluid>
          <v-row>
            <v-col cols="12" sm="6">
              <validation-provider rules="required" :name="$t('findInStore.settings.mapProvider')" v-slot="{ errors }">
                <v-select
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.mapProvider"
                  :label="$t('findInStore.settings.mapProvider')"
                  :items="mapProviderItems"
                  item-text="text"
                  item-value="value"
                  required
                  :error-messages="errors"
                ></v-select>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                rules="required"
                :name="$t('findInStore.settings.geolocationMapApiKey')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.geolocationMapApiKey"
                  :label="$t('findInStore.settings.geolocationMapApiKey')"
                  required
                  :error-messages="errors"
                  clearable
                  :type="showApiKey ? 'text' : 'password'"
                  :append-icon="showApiKey ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showApiKey = !showApiKey"
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                rules="min_value:1|max_value:50|integer"
                :name="$t('findInStore.settings.numberLocationToDisplay')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.numberLocationToDisplay"
                  type="number"
                  :label="$t('findInStore.settings.numberLocationToDisplay')"
                  required
                  :error-messages="errors"
                  clearable
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                :rules="`min_value:${minDistanceRuleValue}|max_value:${maxDistanceRuleValue}`"
                :name="$t('findInStore.settings.storeMaxDistance')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.storeMaxDistance"
                  type="number"
                  :label="$t('findInStore.settings.storeMaxDistance')"
                  required
                  :error-messages="errors"
                  clearable
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6" v-if="isOctipasAdmin">
              <validation-provider
                :rules="`min_value:${minDistanceRuleValue}`"
                :name="$t('findInStore.settings.storeMaxDistanceUpperLimit')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.storeMaxDistanceUpperLimit"
                  @change="adjustMinValue"
                  type="number"
                  :label="$t('findInStore.settings.storeMaxDistanceUpperLimit')"
                  required
                  :error-messages="errors"
                  clearable
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6" v-if="isOctipasAdmin">
              <validation-provider
                :rules="`min_value:0.5|max_value:${maxDistanceRuleValue}`"
                :name="$t('findInStore.settings.storeMinDistance')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.storeMinDistance"
                  @change="adjustMinValue"
                  type="number"
                  :label="$t('findInStore.settings.storeMinDistance')"
                  required
                  :error-messages="errors"
                  clearable
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                :rules="`min_value:${minDistanceRuleValue}|max_value:${settings.storeMaxDistance}`"
                :name="$t('findInStore.settings.defaultSearchRadius')"
                v-slot="{ errors }"
              >
                <v-text-field
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.defaultSearchRadius"
                  type="number"
                  :label="$t('findInStore.settings.defaultSearchRadius')"
                  required
                  :error-messages="errors"
                  clearable
                ></v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                :rules="`required|one_of:${searchUnits.join(',')}`"
                :name="$t('findInStore.settings.defaultSearchUnit')"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  hide-details="auto"
                  class="text-uppercase"
                  outlined
                  dense
                  v-model="settings.defaultSearchUnit"
                  :label="$t('findInStore.settings.defaultSearchUnit')"
                  :items="searchUnits"
                  required
                  :error-messages="errors"
                  clearable
                ></v-autocomplete>
              </validation-provider>
            </v-col>
            <v-col cols="12" sm="6">
              <validation-provider
                :rules="`scope_lists_still_exist:${existingListsIds}`"
                :name="$t('settings.locationScope')"
                v-slot="{ errors }"
              >
                <v-autocomplete
                  :disabled="Array.isArray(fetchedLists) && fetchedLists.length === 0"
                  v-model="locationScopeModel"
                  :items="listItems"
                  :label="$t('settings.locationScope')"
                  :item-text="(item) => `${item.list_id} - ${item.name}`"
                  item-value="list_id"
                  clearable
                  dense
                  hide-details="auto"
                  :error-messages="errors"
                  outlined
                  multiple
                >
                  <template v-slot:prepend-item>
                    <v-list-item ripple @mousedown.prevent @click.stop="toggleAllLocationsLists()" color="primary">
                      <v-list-item-action>
                        <v-simple-checkbox
                          color="primary"
                          :value="allLocationsListsSelected"
                          hide-details
                          @click="toggleAllLocationsLists()"
                        ></v-simple-checkbox>
                      </v-list-item-action>
                      <v-list-item-content>
                        <div>{{ $t('settings.selectAll') }}</div>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">{{ item.name }}</span>
                    <span v-if="index === 1" class="grey--text caption">
                      (+{{ settings.locationScope.length - 1 }} {{ $t('components.select.others') }})
                    </span>
                  </template>
                  <template v-slot:item="data">
                    <v-list-item>
                      <v-list-item-action>
                        <v-checkbox
                          :value="data.item.list_id"
                          :input-value="locationScopeModel"
                          @change="toggleItem(data.item)"
                          color="primary"
                          hide-details
                        />
                      </v-list-item-action>
                      <v-list-item-content>
                        <div>
                          <span :class="{ 'deleted-item': data.item.deleted }">
                            {{ data.item.list_id }} - {{ data.item.name }}
                          </span>
                        </div>
                      </v-list-item-content>
                      <v-list-item-action v-if="data.item.deleted">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon color="red" class="ml-2" v-bind="attrs" v-on="on">mdi-alert-circle-outline</v-icon>
                          </template>
                          <span>{{ $t('findInStore.settings.deletedListTooltip') }}</span>
                        </v-tooltip>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </validation-provider>
            </v-col>
            <v-col v-if="settings.locationScope.length" cols="12" sm="6">
              <v-switch
                class="ma-0"
                v-model="settings.excludeLocationScope"
                :label="$t('settings.excludeLocationScope')"
                :messages="$t('settings.excludeLocationScopeMessage')"
                dense
              ></v-switch>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
      <information-dialog
        v-model="missingListModal"
        persistent
        :title="$t('findInStore.settings.missingListsModalTitle')"
        :introductionSentence="$t('findInStore.settings.missingListsModalText')"
      ></information-dialog>
    </v-container>
  </validation-observer>
</template>
<script>
  import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
  import { mapState } from 'vuex'
  import { ListService } from '@/services/listService'
  import { i18n } from '@/i18n'
  import { EventBus } from '@/event-bus'
  import informationDialog from '@/components/dialogs/InformationDialog.vue'

  // The settings API stores the chosen provider and does not return the allowed list.
  // Keep this aligned with map_settings.provider in the Find in Store API.
  const MAP_PROVIDER_VALUES = ['google', 'karto']

  extend('one_of', (value, values) => {
    return values.indexOf(value) !== -1
  })

  extend('scope_lists_still_exist', {
    // Validation logic
    validate(scope, existingListsIds) {
      const currentScope = Array.isArray(scope) ? scope : []
      return currentScope.every((list) => existingListsIds.includes(list))
    },

    // Error message if validation fails
    message: i18n.t('findInStore.settings.invalidScope'),
  })

  export default {
    name: 'FindInStoreSettingsGeneral',
    components: {
      ValidationProvider,
      ValidationObserver,
      informationDialog,
    },
    props: {
      settings: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapState(['isOctipasAdmin']),
      minDistanceRuleValue() {
        return this.settings.storeMinDistance || 1
      },
      maxDistanceRuleValue() {
        return this.settings.storeMaxDistanceUpperLimit || 100
      },
      noSelectableListDefaultValue() {
        return { name: this.$t('settings.all').toUpperCase(), list_id: 'all' }
      },
      noSelectableListReplacement() {
        return [{ name: this.$t('settings.all').toUpperCase(), list_id: 'all' }]
      },
      mapProviderItems() {
        return MAP_PROVIDER_VALUES.map((value) => ({
          value,
          text: this.$t(`findInStore.settings.mapProviders.${value}`),
        }))
      },
      listItems() {
        // Get the current location scope from settings, default to empty array
        const locationScopeModel = this.settings?.locationScope ?? []

        const finalLists = []
        const existingIds = new Set()

        // Collect all existing list IDs from fetchedLists
        if (Array.isArray(this.fetchedLists) && this.fetchedLists.length) {
          for (const item of this.fetchedLists) {
            existingIds.add(item.list_id)
          }
        }

        // Add missing items from locationScopeModel that are not in fetchedLists
        for (const list of locationScopeModel) {
          if (!existingIds.has(list)) {
            finalLists.push({
              list_id: list,
              name: this.$t('findInStore.settings.missingList'),
              deleted: true,
            }) // Add missing item with deleted flag
          }
        }

        // Add all items from fetchedLists in their original order
        if (Array.isArray(this.fetchedLists) && this.fetchedLists.length) {
          for (const item of this.fetchedLists) {
            finalLists.push(item)
          }
        }

        // Return final list or fallback replacement if empty
        return Array.isArray(finalLists) && finalLists.length ? finalLists : this.noSelectableListReplacement
      },
      locationScopeModel: {
        get() {
          return Array.isArray(this.fetchedLists) && this.fetchedLists.length
            ? this.settings.locationScope
            : this.noSelectableListDefaultValue
        },
        set(value) {
          if (Array.isArray(this.fetchedLists) && this.fetchedLists.length) {
            this.settings.locationScope = value
          }
        },
      },
    },
    data() {
      return {
        showApiKey: false,
        searchUnits: ['km', 'miles'],
        // The API stores a single key: remember the others while editing so switching back restores them.
        mapApiKeysByProvider: { [this.settings.mapProvider]: this.settings.geolocationMapApiKey },
        listService: undefined,
        loading: false,
        fetchedLists: [],
        allLocationsListsSelected: false,
        existingListsIds: [],
        missingListModal: false,
      }
    },
    mounted() {
      this.listService = new ListService(this.$auth.accessToken)
      this.list()
    },
    methods: {
      adjustMinValue() {
        const storeMaxDistanceUpperLimit = this.settings?.storeMaxDistanceUpperLimit ?? 100
        if (Number.parseInt(storeMaxDistanceUpperLimit, 10) < Number.parseInt(this.settings.storeMinDistance, 10)) {
          this.settings.storeMinDistance = Number.parseInt(storeMaxDistanceUpperLimit, 10)
        }
      },
      async hasErrors() {
        const valid = await this.$refs.providerObserver.validate()
        return !valid
      },
      async list() {
        this.loading = true
        const res = await this.listService.list({ type: 'locations' }, false)
        if (res) {
          this.fetchedLists = res.data
          this.checkAllListsAreChecked()
          this.checkScopedListsExistence()
        }
        this.loading = false
      },
      toggleAllLocationsLists() {
        this.allLocationsListsSelected = !this.allLocationsListsSelected
        if (this.allLocationsListsSelected) {
          this.settings.locationScope = this.fetchedLists.map((list) => list.list_id)
        } else {
          this.settings.locationScope = []
        }
      },
      checkAllListsAreChecked() {
        this.allLocationsListsSelected =
          Array.isArray(this.locationScopeModel) && this.locationScopeModel.length >= this.listItems.length
      },
      toggleItem(item) {
        const value = item.list_id
        const index = this.locationScopeModel.indexOf(value)
        if (index === -1) {
          this.locationScopeModel.push(value)
        } else {
          this.locationScopeModel.splice(index, 1)
        }
      },
      checkScopedListsExistence() {
        if (Array.isArray(this.fetchedLists)) {
          this.existingListsIds = this.fetchedLists.map((fetchedList) => fetchedList.list_id)
          if (Array.isArray(this.settings.locationScope) && this.settings.locationScope.length > 0) {
            this.missingListModal = !this.settings.locationScope.every((list) => this.existingListsIds.includes(list))
          }
        }
      }
    },
    watch: {
      // we watch to check as soon as a list is added to the scope, if we have to check the "all selected" checkbox as well
      'settings.locationScope'() {
        this.checkAllListsAreChecked()
      },
      'settings.mapProvider'(provider, previousProvider) {
        if (!previousProvider || provider === previousProvider) {
          return
        }
        const previousKey = this.settings.geolocationMapApiKey
        this.$set(this.mapApiKeysByProvider, previousProvider, previousKey)
        this.settings.geolocationMapApiKey = this.mapApiKeysByProvider[provider]
        this.showApiKey = false
        if (previousKey && !this.settings.geolocationMapApiKey) {
          EventBus.$emit('displaySnackbar', {
            display: true,
            text: this.$t('findInStore.settings.mapProviderKeyCleared'),
            color: 'info',
          })
        }
      },
    },
  }
</script>
<style scoped>
  .deleted-item {
    color: #f44336 !important;
  }
</style>
